import { useState } from "react";
import { Heart, Reply, ChevronDown, ChevronRight, Edit2, Trash2 } from "lucide-react";

function Comment({ comment, storyId, onReply, depth = 0, currentUserId, onEditComment, onDeleteComment }) {
  const [showReplies, setShowReplies] = useState(true);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(comment.content);

  const handleReply = () => {
    if (!replyText.trim()) return;
    onReply(comment.comment_id, replyText);
    setReplyText("");
    setReplying(false);
  };

  const handleEdit = () => {
    if (!editText.trim()) return;
    onEditComment(comment.comment_id, editText);
    setEditing(false);
  };

  const isAuthor = currentUserId === comment.user_id;

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <div className={`${depth > 0 ? "ml-6 pl-4 border-l-2 border-[#E7E5E0]" : ""}`}>
      <div className="flex gap-2 py-2">
        <div className="w-7 h-7 rounded-full overflow-hidden border border-[#E7E5E0] flex-shrink-0">
          <img src={comment.author_avatar} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{comment.author_name}</span>
            <span className="text-xs text-[#6B6B70]">{timeAgo(comment.created_at)}</span>
            {isAuthor && (
              <div className="ml-auto flex gap-2">
                <button
                  onClick={() => { setEditing(!editing); setEditText(comment.content); }}
                  className="text-xs text-[#7C3AED] hover:text-[#6D28D9]"
                >
                  <Edit2 className="w-3 h-3" />
                </button>
                <button
                  onClick={() => onDeleteComment(comment.comment_id)}
                  className="text-xs text-red-500 hover:text-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          {editing ? (
            <div className="flex items-center gap-2 mt-1">
              <input
                type="text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="flex-1 px-2 py-1 bg-[#F5F3EE] border border-[#E7E5E0] rounded text-xs"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleEdit()}
              />
              <button onClick={handleEdit} className="text-xs bg-[#7C3AED] text-white px-2 py-1 rounded">Save</button>
              <button onClick={() => setEditing(false)} className="text-xs bg-gray-200 px-2 py-1 rounded">Cancel</button>
            </div>
          ) : (
            <p className="text-sm text-[#404040] mt-0.5">{comment.content}</p>
          )}

          <button
            onClick={() => setReplying(!replying)}
            className="flex items-center gap-1 text-xs text-[#6B6B70] hover:text-[#0F0F10] mt-1 transition-colors"
          >
            <Reply className="w-3 h-3" /> Reply
          </button>

          {replying && (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply..."
                className="flex-1 px-3 py-1.5 bg-[#F5F3EE] border border-[#E7E5E0] rounded-lg text-xs outline-none focus:border-[#7C3AED]"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleReply()}
              />
              <button
                onClick={handleReply}
                disabled={!replyText.trim()}
                className="text-xs bg-[#7C3AED] text-white px-3 py-1.5 rounded-lg hover:bg-[#6D28D9] disabled:opacity-50"
              >
                Reply
              </button>
            </div>
          )}
        </div>
      </div>

      {comment.replies && comment.replies.length > 0 && (
        <div>
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="flex items-center gap-1 text-xs text-[#7C3AED] hover:text-[#6D28D9] mb-1 transition-colors"
          >
            {showReplies ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {comment.replies.length} {comment.replies.length === 1 ? "reply" : "replies"}
          </button>
          {showReplies && (
            <div>
              {comment.replies.map((reply) => (
                <Comment
                  key={reply.comment_id}
                  comment={reply}
                  storyId={storyId}
                  onReply={onReply}
                  depth={depth + 1}
                  currentUserId={currentUserId}
                  onEditComment={onEditComment}
                  onDeleteComment={onDeleteComment}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CommentSection({ comments, storyId, onAddComment, currentUserId, onEditComment, onDeleteComment }) {
  const [newComment, setNewComment] = useState("");

  const handleSubmit = () => {
    if (!newComment.trim()) return;
    onAddComment(null, newComment);
    setNewComment("");
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-[#0F0F10]">Comments ({comments?.length || 0})</h3>

      {/* New comment input */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment..."
          className="flex-1 px-4 py-2.5 bg-[#F5F3EE] border border-[#E7E5E0] rounded-xl text-sm outline-none focus:border-[#7C3AED] transition-colors"
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        />
        <button
          onClick={handleSubmit}
          disabled={!newComment.trim()}
          className="bg-[#7C3AED] text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-[#6D28D9] disabled:opacity-50 transition-colors"
        >
          Post
        </button>
      </div>

      {/* Comments list */}
      <div className="space-y-1">
        {comments?.map((comment) => (
          <Comment
            key={comment.comment_id}
            comment={comment}
            storyId={storyId}
            onReply={(parentId, text) => onAddComment(parentId, text)}
            currentUserId={currentUserId}
            onEditComment={onEditComment}
            onDeleteComment={onDeleteComment}
          />
        ))}
        {(!comments || comments.length === 0) && (
          <p className="text-sm text-[#6B6B70] text-center py-4">No comments yet. Be the first!</p>
        )}
      </div>
    </div>
  );
}